import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  QueryStateView,
  QueryLoadingState,
  QueryErrorState,
  QueryEmptyState,
  QueryBackgroundSync,
  QueryStaleIndicator,
} from './index';
import { queryKeys } from '../../queries/queryKeys';
import {
  getPersistedCacheStats,
  clearPersistedCache,
  CACHE_STORAGE_KEY,
  CACHE_BUSTER,
} from '../../queries/persister';
import {
  Database,
  RefreshCw,
  Zap,
  Layers,
  AlertCircle,
  Clock,
  PlusCircle,
  Trash2,
  CheckCircle,
  HardDrive,
  ShieldCheck,
  Server,
  User,
  Sliders,
  Bell,
  FileText,
  Check,
} from 'lucide-react';
import {
  useAppDispatch,
  useAppSelector,
  setUser,
  setIsActiveDoctor,
  logout,
  toggleSidebarDrawer,
  incrementUnreadMessages,
  decrementUnreadMessages,
  addAlert,
  clearAllAlerts,
  setDraftObservationNote,
  selectAuthState,
  selectUiState,
  selectConsultationUi,
  selectNotificationsState,
  selectUserDisplayName,
} from '../../store';
import './QueryStateView.scss';

// Mock server state storage for showcase simulation
let mockServerObservations = [
  { id: 'obs-1', observation: 'Patient shows normal cardiac rhythm and BP (120/80)', date: '2026-08-01' },
  { id: 'obs-2', observation: 'Follow-up on allergy reaction to Penicillin - resolved', date: '2026-08-03' },
  { id: 'obs-3', observation: 'Recommended routine blood panel before next quarter', date: '2026-08-05' },
];

export const QueryShowcase = () => {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const authState = useAppSelector(selectAuthState);
  const uiState = useAppSelector(selectUiState);
  const consultationUiState = useAppSelector(selectConsultationUi);
  const notificationsState = useAppSelector(selectNotificationsState);
  const userDisplayName = useAppSelector(selectUserDisplayName);

  const [activeTab, setActiveTab] = useState('demo-states');
  const [simulatedMode, setSimulatedMode] = useState('normal'); // 'normal' | 'loading' | 'error' | 'empty'
  const [newObsText, setNewObsText] = useState('');
  const [shouldFailMutation, setShouldFailMutation] = useState(false);
  const [mutationMessage, setMutationMessage] = useState('');
  const [cacheStats, setCacheStats] = useState(() => getPersistedCacheStats());

  const refreshStats = () => {
    setCacheStats(getPersistedCacheStats());
  };

  useEffect(() => {
    refreshStats();
    const interval = setInterval(refreshStats, 2000);
    return () => clearInterval(interval);
  }, []);

  const patientId = 'demo-patient-101';

  // Live Query Hook using domain keys
  const observationQuery = useQuery({
    queryKey: queryKeys.observations.byPatient(patientId),
    queryFn: async () => {
      // Delay to demonstrate loading / background-fetching
      await new Promise((res) => setTimeout(res, 800));

      if (simulatedMode === 'error') {
        throw new Error('Simulated HTTP 500: Database connection timeout');
      }
      if (simulatedMode === 'empty') {
        return [];
      }
      return [...mockServerObservations];
    },
    staleTime: 10 * 1000, // 10 seconds staleTime for easy live testing of staleness
  });

  // Optimistic Mutation Hook
  const addObservationMutation = useMutation({
    mutationFn: async (text) => {
      await new Promise((res) => setTimeout(res, 1200));
      if (shouldFailMutation) {
        throw new Error('Server rejected mutation: Network validation failed');
      }
      const newEntry = {
        id: `obs-${Date.now()}`,
        observation: text,
        date: new Date().toISOString().split('T')[0],
      };
      mockServerObservations = [newEntry, ...mockServerObservations];
      return newEntry;
    },
    onMutate: async (text) => {
      const queryKey = queryKeys.observations.byPatient(patientId);
      await queryClient.cancelQueries({ queryKey });
      const previousData = queryClient.getQueryData(queryKey) || [];

      const optimisticEntry = {
        id: `temp-${Date.now()}`,
        observation: text,
        date: 'Saving optimistically...',
        is_pending: true,
      };

      queryClient.setQueryData(queryKey, (old = []) => [optimisticEntry, ...old]);
      setMutationMessage('⚡ Optimistic write applied instantly to UI cache!');

      return { previousData, queryKey };
    },
    onError: (err, text, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(context.queryKey, context.previousData);
      }
      setMutationMessage(`❌ Mutation failed (${err.message}). Rolled back to previous state!`);
    },
    onSuccess: () => {
      setMutationMessage('✅ Mutation confirmed by server. Targeted cache synchronized!');
    },
    onSettled: (_data, _error, _variables, context) => {
      if (context?.queryKey) {
        queryClient.invalidateQueries({ queryKey: context.queryKey });
      }
      refreshStats();
    },
  });

  const handleAddSubmit = (e) => {
    e.preventDefault();
    if (!newObsText.trim()) return;
    addObservationMutation.mutate(newObsText.trim());
    setNewObsText('');
  };

  const handleResetServerData = () => {
    mockServerObservations = [
      { id: 'obs-1', observation: 'Patient shows normal cardiac rhythm and BP (120/80)', date: '2026-08-01' },
      { id: 'obs-2', observation: 'Follow-up on allergy reaction to Penicillin - resolved', date: '2026-08-03' },
      { id: 'obs-3', observation: 'Recommended routine blood panel before next quarter', date: '2026-08-05' },
    ];
    queryClient.invalidateQueries({ queryKey: queryKeys.observations.byPatient(patientId) });
    refreshStats();
  };

  const handleClearPersistedCache = () => {
    clearPersistedCache(queryClient);
    refreshStats();
    setMutationMessage('🧹 Persisted cache cleared from storage and memory!');
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1100px', margin: '0 auto', fontFamily: 'system-ui, sans-serif' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ padding: '10px', borderRadius: '12px', background: '#0284c7', color: 'white' }}>
            <Database size={28} />
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.6rem', color: '#0f172a' }}>TanStack Query Server-State Architecture</h2>
            <p style={{ margin: 0, color: '#64748b', fontSize: '0.9rem' }}>
              Persistent Cache • Domain Keys • Colocated Queries • Optimistic Mutations • 5 Rich States
            </p>
          </div>
        </div>

        {/* Persistence pill */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 14px',
          borderRadius: '20px',
          background: cacheStats.exists ? '#f0fdf4' : '#f8fafc',
          border: cacheStats.exists ? '1px solid #bbf7d0' : '1px solid #e2e8f0',
          fontSize: '0.82rem',
          color: cacheStats.exists ? '#15803d' : '#64748b',
          fontWeight: 600,
        }}>
          <HardDrive size={15} />
          <span>Persisted: {cacheStats.formattedSize} ({cacheStats.queryCount} queries)</span>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '10px', borderBottom: '2px solid #e2e8f0', marginBottom: '24px' }}>
        <button
          type="button"
          onClick={() => setActiveTab('demo-states')}
          style={{
            padding: '10px 18px',
            border: 'none',
            background: 'none',
            fontWeight: 600,
            cursor: 'pointer',
            borderBottom: activeTab === 'demo-states' ? '3px solid #0284c7' : '3px solid transparent',
            color: activeTab === 'demo-states' ? '#0284c7' : '#64748b',
          }}
        >
          1. 5 Server-Data States Showcase
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('optimistic-mutations')}
          style={{
            padding: '10px 18px',
            border: 'none',
            background: 'none',
            fontWeight: 600,
            cursor: 'pointer',
            borderBottom: activeTab === 'optimistic-mutations' ? '3px solid #0284c7' : '3px solid transparent',
            color: activeTab === 'optimistic-mutations' ? '#0284c7' : '#64748b',
          }}
        >
          2. Optimistic Mutations & Rollback
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('keys-inspector')}
          style={{
            padding: '10px 18px',
            border: 'none',
            background: 'none',
            fontWeight: 600,
            cursor: 'pointer',
            borderBottom: activeTab === 'keys-inspector' ? '3px solid #0284c7' : '3px solid transparent',
            color: activeTab === 'keys-inspector' ? '#0284c7' : '#64748b',
          }}
        >
          3. Domain Key Hierarchy & Cache
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('persistence-manager')}
          style={{
            padding: '10px 18px',
            border: 'none',
            background: 'none',
            fontWeight: 600,
            cursor: 'pointer',
            borderBottom: activeTab === 'persistence-manager' ? '3px solid #0284c7' : '3px solid transparent',
            color: activeTab === 'persistence-manager' ? '#0284c7' : '#64748b',
          }}
        >
          4. Persistent Cache Manager
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('redux-state')}
          style={{
            padding: '10px 18px',
            border: 'none',
            background: 'none',
            fontWeight: 600,
            cursor: 'pointer',
            borderBottom: activeTab === 'redux-state' ? '3px solid #7c3aed' : '3px solid transparent',
            color: activeTab === 'redux-state' ? '#7c3aed' : '#64748b',
          }}
        >
          5. Redux Global State (RTK)
        </button>
      </div>

      {/* TAB 1: 5 STATES */}
      {activeTab === 'demo-states' && (
        <div>
          {/* Controls Bar */}
          <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', border: '1px solid #e2e8f0' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>Simulate Server Condition:</span>
            <button
              type="button"
              onClick={() => { setSimulatedMode('normal'); observationQuery.refetch(); }}
              style={{ padding: '6px 12px', borderRadius: '8px', border: simulatedMode === 'normal' ? '2px solid #0284c7' : '1px solid #cbd5e1', background: simulatedMode === 'normal' ? '#e0f2fe' : 'white', cursor: 'pointer', fontWeight: 500 }}
            >
              Normal (Success)
            </button>
            <button
              type="button"
              onClick={() => { setSimulatedMode('empty'); observationQuery.refetch(); }}
              style={{ padding: '6px 12px', borderRadius: '8px', border: simulatedMode === 'empty' ? '2px solid #0284c7' : '1px solid #cbd5e1', background: simulatedMode === 'empty' ? '#e0f2fe' : 'white', cursor: 'pointer', fontWeight: 500 }}
            >
              Empty Data
            </button>
            <button
              type="button"
              onClick={() => { setSimulatedMode('error'); observationQuery.refetch(); }}
              style={{ padding: '6px 12px', borderRadius: '8px', border: simulatedMode === 'error' ? '2px solid #dc2626' : '1px solid #cbd5e1', background: simulatedMode === 'error' ? '#fee2e2' : 'white', color: simulatedMode === 'error' ? '#991b1b' : 'inherit', cursor: 'pointer', fontWeight: 500 }}
            >
              Server Error (500)
            </button>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
              <button
                type="button"
                onClick={() => observationQuery.refetch()}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '8px', background: '#0284c7', color: 'white', border: 'none', cursor: 'pointer', fontWeight: 500 }}
              >
                <RefreshCw size={14} className={observationQuery.isFetching ? 'animate-spin' : ''} />
                Trigger Background Refetch
              </button>
            </div>
          </div>

          {/* State View Rendering */}
          <div style={{ border: '1px solid #e2e8f0', borderRadius: '16px', padding: '24px', background: '#ffffff', boxShadow: '0 2px 10px rgba(0,0,0,0.02)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#1e293b' }}>Patient Observations</h3>
                <span style={{ fontSize: '0.8rem', color: '#64748b' }}>Query Key: <code>{JSON.stringify(queryKeys.observations.byPatient(patientId))}</code></span>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <QueryBackgroundSync isFetching={observationQuery.isFetching} isLoading={observationQuery.isLoading} />
                <QueryStaleIndicator
                  isStale={observationQuery.isStale}
                  dataUpdatedAt={observationQuery.dataUpdatedAt}
                  onRefresh={() => observationQuery.refetch()}
                  isFetching={observationQuery.isFetching}
                />
              </div>
            </div>

            <QueryStateView
              query={observationQuery}
              loadingVariant="cards"
              loadingCount={3}
              emptyTitle="No observations recorded"
              emptyDescription="No clinical observations or timeline notes exist for this patient yet."
              emptyActionLabel="Add First Observation"
              onEmptyAction={() => { setSimulatedMode('normal'); observationQuery.refetch(); }}
              showBackgroundSync={false}
              showStaleIndicator={false}
            >
              {({ data }) => (
                <div style={{ display: 'grid', gap: '12px' }}>
                  {data?.map((item) => (
                    <div
                      key={item.id}
                      style={{
                        padding: '16px',
                        borderRadius: '12px',
                        border: item.is_pending ? '1.5px dashed #0284c7' : '1px solid #e2e8f0',
                        background: item.is_pending ? '#f0f9ff' : '#f8fafc',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: '4px' }}>
                          {item.observation}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                          Recorded on: {item.date}
                        </div>
                      </div>
                      {item.is_pending ? (
                        <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#0284c7', background: '#e0f2fe', padding: '4px 8px', borderRadius: '6px' }}>
                          Optimistic (Syncing...)
                        </span>
                      ) : (
                        <CheckCircle size={18} color="#10b981" />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </QueryStateView>
          </div>
        </div>
      )}

      {/* TAB 2: OPTIMISTIC MUTATIONS */}
      {activeTab === 'optimistic-mutations' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          {/* Controls & Form */}
          <div style={{ border: '1px solid #e2e8f0', borderRadius: '16px', padding: '24px', background: '#ffffff' }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '1.2rem', color: '#1e293b' }}>Add Observation (Optimistic)</h3>
            <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '20px' }}>
              The UI updates <strong>immediately</strong> before the server request completes. If the server errors, the cache automatically <strong>rolls back</strong> to the previous state.
            </p>

            <form onSubmit={handleAddSubmit}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '6px' }}>
                  Observation Notes:
                </label>
                <textarea
                  value={newObsText}
                  onChange={(e) => setNewObsText(e.target.value)}
                  placeholder="e.g. Prescribed inhaler for seasonal asthma"
                  rows={3}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  id="fail-mutation-chk"
                  checked={shouldFailMutation}
                  onChange={(e) => setShouldFailMutation(e.target.checked)}
                />
                <label htmlFor="fail-mutation-chk" style={{ fontSize: '0.85rem', color: '#b91c1c', fontWeight: 500, cursor: 'pointer' }}>
                  Simulate Server Failure (Test Rollback)
                </label>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="submit"
                  disabled={!newObsText.trim() || addObservationMutation.isPending}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '10px 18px',
                    borderRadius: '8px',
                    background: '#0284c7',
                    color: 'white',
                    border: 'none',
                    fontWeight: 600,
                    cursor: !newObsText.trim() ? 'not-allowed' : 'pointer',
                    opacity: !newObsText.trim() ? 0.6 : 1,
                  }}
                >
                  <PlusCircle size={16} />
                  Add Optimistically
                </button>
                <button
                  type="button"
                  onClick={handleResetServerData}
                  style={{ padding: '10px 14px', borderRadius: '8px', background: '#f1f5f9', border: '1px solid #cbd5e1', cursor: 'pointer' }}
                >
                  Reset Server DB
                </button>
              </div>
            </form>

            {mutationMessage && (
              <div style={{ marginTop: '18px', padding: '12px', borderRadius: '8px', background: '#f8fafc', border: '1px solid #e2e8f0', fontSize: '0.85rem' }}>
                {mutationMessage}
              </div>
            )}
          </div>

          {/* Live Cache View */}
          <div style={{ border: '1px solid #e2e8f0', borderRadius: '16px', padding: '24px', background: '#ffffff' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#1e293b' }}>Live Cache State</h3>
              <QueryBackgroundSync isFetching={observationQuery.isFetching} />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {observationQuery.data?.map((obs) => (
                <div
                  key={obs.id}
                  style={{
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: obs.is_pending ? '1.5px dashed #0284c7' : '1px solid #e2e8f0',
                    background: obs.is_pending ? '#e0f2fe' : '#f8fafc',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div>
                    <div style={{ fontSize: '0.9rem', fontWeight: 500, color: '#1e293b' }}>{obs.observation}</div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b' }}>ID: {obs.id} • {obs.date}</div>
                  </div>
                  {obs.is_pending && <span style={{ fontSize: '0.7rem', color: '#0369a1', fontWeight: 600 }}>OPTIMISTIC</span>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* TAB 3: KEYS INSPECTOR */}
      {activeTab === 'keys-inspector' && (
        <div style={{ border: '1px solid #e2e8f0', borderRadius: '16px', padding: '24px', background: '#ffffff' }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '1.2rem', color: '#1e293b' }}>Domain-Shaped Query Key Hierarchy</h3>
          <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '20px' }}>
            Query keys are structured hierarchically to enable surgical, targeted invalidation without wiping unrelated caches.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
            <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#0284c7', fontSize: '0.95rem' }}>🏥 Appointments Domain</h4>
              <pre style={{ margin: 0, fontSize: '0.78rem', color: '#334155' }}>
{`queryKeys.appointments.all
queryKeys.appointments.lists()
queryKeys.appointments.list({ status: 'confirmed' })
queryKeys.appointments.detail(123)
queryKeys.appointments.messages(123)
queryKeys.appointments.availability(45, '2026-08-10')`}
              </pre>
            </div>

            <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#0284c7', fontSize: '0.95rem' }}>👤 Patients Domain</h4>
              <pre style={{ margin: 0, fontSize: '0.78rem', color: '#334155' }}>
{`queryKeys.patients.all
queryKeys.patients.detail(101)
queryKeys.patients.members(101)
queryKeys.patients.timeline(101)
queryKeys.patients.indicators(101)`}
              </pre>
            </div>

            <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#0284c7', fontSize: '0.95rem' }}>📋 Observations Domain</h4>
              <pre style={{ margin: 0, fontSize: '0.78rem', color: '#334155' }}>
{`queryKeys.observations.all
queryKeys.observations.byPatient(101)
queryKeys.observations.timeline(101)
queryKeys.observations.templates()`}
              </pre>
            </div>

            <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#0284c7', fontSize: '0.95rem' }}>🚪 Waiting Room Domain</h4>
              <pre style={{ margin: 0, fontSize: '0.78rem', color: '#334155' }}>
{`queryKeys.waitingRoom.all
queryKeys.waitingRoom.byDoctor(45)
queryKeys.waitingRoom.patients(12)`}
              </pre>
            </div>
          </div>
        </div>
      )}

      {/* TAB 4: PERSISTENT CACHE MANAGER */}
      {activeTab === 'persistence-manager' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '20px' }}>
          {/* Left: Stats & Controls */}
          <div style={{ border: '1px solid #e2e8f0', borderRadius: '16px', padding: '24px', background: '#ffffff' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
              <HardDrive size={22} color="#0284c7" />
              <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#1e293b' }}>Persistent Cache Engine</h3>
            </div>
            <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '20px' }}>
              TanStack Query cache is automatically serialized to browser storage (with automatic throttling and schema versioning).
              Data survives page reloads, tab closure, and network interruptions.
            </p>

            {/* Metrics cards */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '24px' }}>
              <div style={{ padding: '14px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>STORAGE KEY</div>
                <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a', marginTop: '4px' }}>
                  <code>{CACHE_STORAGE_KEY}</code>
                </div>
              </div>

              <div style={{ padding: '14px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>CACHE BUSTER</div>
                <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#0284c7', marginTop: '4px' }}>
                  <code>{CACHE_BUSTER}</code>
                </div>
              </div>

              <div style={{ padding: '14px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>PERSISTED SIZE</div>
                <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#10b981', marginTop: '4px' }}>
                  {cacheStats.formattedSize}
                </div>
              </div>

              <div style={{ padding: '14px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>PERSISTED QUERIES</div>
                <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#6366f1', marginTop: '4px' }}>
                  {cacheStats.queryCount}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                type="button"
                onClick={refreshStats}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '10px 16px',
                  borderRadius: '8px',
                  background: '#0284c7',
                  color: 'white',
                  border: 'none',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                <RefreshCw size={15} />
                Refresh Cache Stats
              </button>

              <button
                type="button"
                onClick={handleClearPersistedCache}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '10px 16px',
                  borderRadius: '8px',
                  background: '#fee2e2',
                  color: '#dc2626',
                  border: '1px solid #fecaca',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                <Trash2 size={15} />
                Clear Persisted Cache
              </button>
            </div>
          </div>

          {/* Right: Security & Dehydration Rules */}
          <div style={{ border: '1px solid #e2e8f0', borderRadius: '16px', padding: '24px', background: '#ffffff' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
              <ShieldCheck size={20} color="#16a34a" />
              <h4 style={{ margin: 0, fontSize: '1.05rem', color: '#1e293b' }}>Privacy & Dehydration Rules</h4>
            </div>

            <ul style={{ paddingLeft: '20px', fontSize: '0.85rem', color: '#334155', lineHeight: 1.6, margin: 0 }}>
              <li style={{ marginBottom: '8px' }}>
                <strong>Success Status Only:</strong> Errored or pending queries are excluded to prevent storage corruption.
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>Opt-out Meta:</strong> Sensitive queries can opt out using <code>meta: &#123; persist: false &#125;</code>.
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>Session Purge on Logout:</strong> The persisted storage is instantly wiped when a user logs out to uphold HIPAA/patient confidentiality.
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>Automatic Version Buster:</strong> Bumping <code>CACHE_BUSTER</code> invalidates and clears stale cache structures across all client devices.
              </li>
              <li>
                <strong>Throttled Writes:</strong> Cache changes are debounced by 1000ms to preserve battery and I/O performance.
              </li>
            </ul>
          </div>
        </div>
      )}

      {/* TAB 5: REDUX GLOBAL STATE */}
      {activeTab === 'redux-state' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '20px' }}>
          {/* Left: Interactive Slice Controls */}
          <div style={{ border: '1px solid #e2e8f0', borderRadius: '16px', padding: '24px', background: '#ffffff' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
              <Layers size={22} color="#7c3aed" />
              <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#1e293b' }}>Redux Toolkit Client State Controls</h3>
            </div>
            <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '20px' }}>
              Global client-side UI, session, and workspace states are managed in Redux with RTK slices, memoized selectors, and typed actions.
            </p>

            {/* Section 1: Auth & User Session */}
            <div style={{ marginBottom: '20px', padding: '14px', background: '#faf5ff', borderRadius: '10px', border: '1px solid #f3e8ff' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#6b21a8' }}>👤 authSlice</span>
                <span style={{ fontSize: '0.75rem', padding: '2px 8px', borderRadius: '12px', background: authState.isAuthenticated ? '#dcfce7' : '#f1f5f9', color: authState.isAuthenticated ? '#166534' : '#64748b', fontWeight: 600 }}>
                  {authState.isAuthenticated ? 'AUTHENTICATED' : 'ANONYMOUS'}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => dispatch(setUser({ id: 45, doctor_id: 45, first_name: 'Sarah', last_name: 'Connor', role: 'doctor' }))}
                  style={{ padding: '6px 12px', borderRadius: '6px', background: '#7c3aed', color: 'white', border: 'none', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}
                >
                  Set Doctor User
                </button>
                <button
                  type="button"
                  onClick={() => dispatch(setIsActiveDoctor(!authState.isActiveDoctor))}
                  style={{ padding: '6px 12px', borderRadius: '6px', background: authState.isActiveDoctor ? '#dcfce7' : '#f1f5f9', color: authState.isActiveDoctor ? '#15803d' : '#334155', border: '1px solid #cbd5e1', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}
                >
                  Toggle Doctor Mode: {authState.isActiveDoctor ? 'ON' : 'OFF'}
                </button>
                <button
                  type="button"
                  onClick={() => dispatch(logout())}
                  style={{ padding: '6px 12px', borderRadius: '6px', background: '#fee2e2', color: '#dc2626', border: '1px solid #fecaca', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}
                >
                  Logout (Clear Auth)
                </button>
              </div>
            </div>

            {/* Section 2: UI Drawer & Theme */}
            <div style={{ marginBottom: '20px', padding: '14px', background: '#f0fdf4', borderRadius: '10px', border: '1px solid #dcfce7' }}>
              <span style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, color: '#166534', marginBottom: '10px' }}>🎨 uiSlice</span>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => dispatch(toggleSidebarDrawer())}
                  style={{ padding: '6px 12px', borderRadius: '6px', background: uiState.sidebarDrawerOpen ? '#16a34a' : '#ffffff', color: uiState.sidebarDrawerOpen ? '#ffffff' : '#334155', border: '1px solid #86efac', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}
                >
                  Sidebar Drawer: {uiState.sidebarDrawerOpen ? 'OPEN' : 'CLOSED'}
                </button>
              </div>
            </div>

            {/* Section 3: Notification Badges */}
            <div style={{ marginBottom: '20px', padding: '14px', background: '#eff6ff', borderRadius: '10px', border: '1px solid #dbeafe' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e40af' }}>🔔 notificationSlice</span>
                <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#1d4ed8' }}>
                  Unread: {notificationsState.unreadMessagesCount}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => dispatch(incrementUnreadMessages())}
                  style={{ padding: '6px 12px', borderRadius: '6px', background: '#2563eb', color: 'white', border: 'none', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}
                >
                  +1 Unread Msg
                </button>
                <button
                  type="button"
                  onClick={() => dispatch(decrementUnreadMessages())}
                  style={{ padding: '6px 12px', borderRadius: '6px', background: '#ffffff', color: '#1e40af', border: '1px solid #bfdbfe', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}
                >
                  -1 Unread Msg
                </button>
                <button
                  type="button"
                  onClick={() => dispatch(addAlert({ title: 'New Lab Result', message: 'Blood chemistry ready for review' }))}
                  style={{ padding: '6px 12px', borderRadius: '6px', background: '#0284c7', color: 'white', border: 'none', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}
                >
                  + Add Alert Toast
                </button>
                <button
                  type="button"
                  onClick={() => dispatch(clearAllAlerts())}
                  style={{ padding: '6px 12px', borderRadius: '6px', background: '#ffffff', color: '#64748b', border: '1px solid #cbd5e1', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}
                >
                  Clear Alerts
                </button>
              </div>
            </div>

            {/* Section 4: Consultation UI Draft */}
            <div style={{ padding: '14px', background: '#fffbeb', borderRadius: '10px', border: '1px solid #fef3c7' }}>
              <span style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, color: '#92400e', marginBottom: '10px' }}>📝 consultationUiSlice (Draft State)</span>
              <input
                type="text"
                value={consultationUiState.draftObservationNote}
                onChange={(e) => dispatch(setDraftObservationNote(e.target.value))}
                placeholder="Type draft note (synchronized via Redux)..."
                style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem' }}
              />
            </div>
          </div>

          {/* Right: Live Redux State Inspector */}
          <div style={{ border: '1px solid #e2e8f0', borderRadius: '16px', padding: '24px', background: '#ffffff' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
              <h4 style={{ margin: 0, fontSize: '1.05rem', color: '#1e293b' }}>Live Redux RootState</h4>
              <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#7c3aed' }}>User: {userDisplayName}</span>
            </div>

            <pre style={{
              background: '#0f172a',
              color: '#38bdf8',
              padding: '16px',
              borderRadius: '10px',
              fontSize: '0.76rem',
              overflow: 'auto',
              maxHeight: '440px',
              lineHeight: 1.4,
              margin: 0,
            }}>
{JSON.stringify(
  {
    auth: authState,
    ui: uiState,
    consultationUi: consultationUiState,
    notifications: notificationsState,
  },
  null,
  2
)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

export default QueryShowcase;
